import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, History, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { crmApi, userHasCrmAccess } from '@/lib/crmApi';
import { getApiBaseUrl } from '@/lib/apiBase';
import { cn } from '@/lib/utils';
import axios from 'axios';

const API = getApiBaseUrl();

function formatMoney(value, currency = '₸') {
  const rounded = Math.round(Number(value) || 0);
  return `${rounded.toLocaleString('ru-RU')} ${currency}`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ru-RU');
}

function SummaryBar({ summary, amountLabel, amountCurrency = '₸' }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-2">
        <div className="text-[11px] text-gray-400">Всего шт</div>
        <div className="text-sm font-semibold text-gray-900">
          {(summary.total_qty || 0).toLocaleString('ru-RU')}
        </div>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-2">
        <div className="text-[11px] text-gray-400">{amountLabel}</div>
        <div className="text-sm font-semibold text-gray-900">
          {formatMoney(summary.total_amount, amountCurrency)}
        </div>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-2">
        <div className="text-[11px] text-gray-400">Последняя</div>
        <div className="text-sm font-semibold text-gray-900">
          {formatShortDate(summary.last_date)}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center px-4">
      <History className="w-8 h-8 text-gray-300 mb-2" />
      <p className="text-sm text-gray-500">{text}</p>
    </div>
  );
}

function SalesTable({ lines }) {
  if (!lines?.length) {
    return <EmptyState text="Продаж по этой позиции пока нет" />;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/80 text-left">
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Дата</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Заказ</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Клиент</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500 text-right">Кол-во</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500 text-right">Цена</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500 text-right">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.item_id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
              <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap align-top">
                {formatDate(line.order_date)}
              </td>
              <td className="py-2.5 px-3 align-top">
                <Link
                  to={`/crm/customer-orders/${line.order_id}/edit`}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {line.order_number || `#${line.order_id}`}
                </Link>
                <div className="text-xs text-gray-400 mt-0.5">{line.order_status_label}</div>
              </td>
              <td className="py-2.5 px-3 text-gray-700 align-top">
                {line.client_name || '—'}
              </td>
              <td className="py-2.5 px-3 text-right text-gray-800 align-top tabular-nums">
                {line.quantity}
              </td>
              <td className="py-2.5 px-3 text-right text-gray-700 align-top tabular-nums">
                {formatMoney(line.price_with_discount || line.price)}
                {line.discount_percent > 0 && (
                  <div className="text-[11px] text-amber-600">−{line.discount_percent}%</div>
                )}
              </td>
              <td className="py-2.5 px-3 text-right font-medium text-gray-900 align-top tabular-nums">
                {formatMoney(line.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PurchasesTable({ lines }) {
  if (!lines?.length) {
    return <EmptyState text="Закупок по этой позиции пока нет" />;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/80 text-left">
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Дата</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Заказ</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Поставщик</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500 text-right">Кол-во</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500 text-right">Закуп</th>
            <th className="py-2.5 px-3 text-xs font-medium text-gray-500">Для кого</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, idx) => (
            <tr
              key={`${line.order_id}-${idx}`}
              className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60"
            >
              <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap align-top">
                {formatDate(line.order_date)}
              </td>
              <td className="py-2.5 px-3 align-top">
                <Link
                  to={`/orders?q=${encodeURIComponent(line.order_number || line.product_code || '')}`}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {line.order_number || line.order_id}
                </Link>
                <div className="text-xs text-gray-400 mt-0.5">{line.order_status}</div>
              </td>
              <td className="py-2.5 px-3 text-gray-700 align-top">
                {line.supplier_name || '—'}
              </td>
              <td className="py-2.5 px-3 text-right text-gray-800 align-top tabular-nums">
                {line.quantity}
              </td>
              <td className="py-2.5 px-3 text-right text-orange-800 align-top tabular-nums">
                {formatMoney(line.supplier_price, line.supplier_currency === 'RUB' ? '₽' : '₸')}
                <div className="text-[11px] text-gray-400">
                  {formatMoney(line.supplier_subtotal, line.supplier_currency === 'RUB' ? '₽' : '₸')}
                </div>
              </td>
              <td className="py-2.5 px-3 text-gray-600 align-top">
                {line.for_client && (line.client_name || line.customer_order_number) ? (
                  <div>
                    <div className="text-gray-800">{line.client_name || 'Клиент'}</div>
                    {line.customer_order_number && (
                      <div className="text-xs text-gray-400">{line.customer_order_number}</div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400">Склад</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProductMovementHistoryModal({
  product,
  open,
  onClose,
  token,
  user,
}) {
  const [tab, setTab] = useState('sales');
  const [sales, setSales] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [salesError, setSalesError] = useState('');
  const [purchasesError, setPurchasesError] = useState('');

  const canReadCrm = userHasCrmAccess(user);

  useEffect(() => {
    if (!open || !product?.id || !token) return;

    let cancelled = false;

    const loadSales = async () => {
      if (!canReadCrm) {
        setSales(null);
        setSalesError('Нет доступа к CRM — продажи недоступны');
        return;
      }
      setSalesLoading(true);
      setSalesError('');
      try {
        const data = await crmApi.getProductSalesHistory(token, {
          product_id: product.id,
          limit: 50,
        });
        if (!cancelled) setSales(data);
      } catch (err) {
        if (!cancelled) {
          setSales(null);
          setSalesError(
            err?.response?.data?.detail || err?.message || 'Не удалось загрузить продажи'
          );
        }
      } finally {
        if (!cancelled) setSalesLoading(false);
      }
    };

    const loadPurchases = async () => {
      setPurchasesLoading(true);
      setPurchasesError('');
      try {
        const { data } = await axios.get(`${API}/products/${product.id}/purchase-history`, {
          params: { limit: 50 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPurchases(data);
      } catch (err) {
        if (!cancelled) {
          setPurchases(null);
          setPurchasesError(
            err?.response?.data?.detail || err?.message || 'Не удалось загрузить закупки'
          );
        }
      } finally {
        if (!cancelled) setPurchasesLoading(false);
      }
    };

    loadSales();
    loadPurchases();

    return () => {
      cancelled = true;
    };
  }, [open, product?.id, product?.code, product?.oem, product?.articul, token, canReadCrm]);

  useEffect(() => {
    if (!open) {
      setTab('sales');
      setSales(null);
      setPurchases(null);
      setSalesError('');
      setPurchasesError('');
    }
  }, [open]);

  if (!product) return null;

  const salesCount = sales?.total ?? null;
  const purchasesCount = purchases?.total ?? null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base pr-8">
            <History className="w-5 h-5 text-gray-500 shrink-0" />
            <span className="truncate">История: {product.name}</span>
          </DialogTitle>
          <p className="text-xs text-gray-400 mt-1 font-mono">
            {[product.code, product.oem || product.articul].filter(Boolean).join(' · ') || product.id}
          </p>
        </DialogHeader>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="sales" className="gap-1.5">
                <ArrowUpRight className="w-3.5 h-3.5" />
                Продажи
                {salesCount != null && (
                  <span
                    className={cn(
                      'ml-1 text-[10px] px-1.5 py-0.5 rounded-full',
                      tab === 'sales' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                    )}
                  >
                    {salesCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="purchases" className="gap-1.5">
                <ArrowDownLeft className="w-3.5 h-3.5" />
                Закупки
                {purchasesCount != null && (
                  <span
                    className={cn(
                      'ml-1 text-[10px] px-1.5 py-0.5 rounded-full',
                      tab === 'purchases' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                    )}
                  >
                    {purchasesCount}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sales" className="space-y-3 mt-0">
              {salesLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Загрузка продаж…
                </div>
              ) : salesError ? (
                <EmptyState text={salesError} />
              ) : (
                <>
                  <SummaryBar summary={sales?.summary} amountLabel="Выручка" />
                  <SalesTable lines={sales?.lines} />
                </>
              )}
            </TabsContent>

            <TabsContent value="purchases" className="space-y-3 mt-0">
              {purchasesLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Загрузка закупок…
                </div>
              ) : purchasesError ? (
                <EmptyState text={purchasesError} />
              ) : (
                <>
                  <SummaryBar
                    summary={purchases?.summary}
                    amountLabel="Сумма закупа"
                    amountCurrency={
                      purchases?.lines?.[0]?.supplier_currency === 'RUB' ? '₽' : '₸'
                    }
                  />
                  <PurchasesTable lines={purchases?.lines} />
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
